import { formatCompactLoggedDuration } from "../../utils/workItemLoggedTimeDisplay";

/** Parse "11h 30m", "4:00", "4:00 hr", or raw minutes into total minutes. */
export function parseDurationToMinutes(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.round(value));
  }

  const s = String(value).trim();

  if (s.includes(":")) {
    const cleaned = s.replace(/[^\d:]/g, "");
    const match = cleaned.match(/(\d+):(\d+)/);
    if (match) {
      const hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      if (Number.isFinite(hours) && Number.isFinite(minutes)) {
        return hours * 60 + minutes;
      }
    }
  }

  const compact = s.match(/(\d+)\s*h(?:\s*(\d+)\s*m)?/i);
  if (compact) {
    const hours = parseInt(compact[1], 10) || 0;
    const minutes = compact[2] != null ? parseInt(compact[2], 10) || 0 : 0;
    return hours * 60 + minutes;
  }

  const minutesOnly = s.match(/^(\d+)\s*m$/i);
  if (minutesOnly) return parseInt(minutesOnly[1], 10) || 0;

  return 0;
}

/** Display duration in compact form (e.g. `11h 30m`, `40m`, `3h`). */
export function formatDurationWithUnit(value) {
  if (value == null || value === "") return "0m";
  if (typeof value === "number" && Number.isFinite(value)) {
    return formatCompactLoggedDuration(value);
  }

  const s = String(value).trim();
  if (/\d+h|\d+m/i.test(s) && !/:/.test(s)) {
    return s.replace(/\s+/g, " ");
  }

  return formatCompactLoggedDuration(parseDurationToMinutes(s));
}

function sumTimesheetGroupEntries(groups) {
  let billableMinutes = 0;
  let loggedMinutes = 0;

  for (const group of Array.isArray(groups) ? groups : []) {
    for (const entry of group?.entries ?? []) {
      const logged =
        entry?.minutesLogged != null && Number.isFinite(Number(entry.minutesLogged))
          ? Number(entry.minutesLogged)
          : parseDurationToMinutes(entry?.duration);
      if (logged <= 0) continue;

      loggedMinutes += logged;
      const billable =
        entry?.billableMinutes != null && Number.isFinite(Number(entry.billableMinutes))
          ? Math.min(Number(entry.billableMinutes), logged)
          : entry?.billable === false
            ? 0
            : logged;
      billableMinutes += billable;
    }
  }

  return {
    total: formatDurationWithUnit(loggedMinutes),
    billable: formatDurationWithUnit(billableMinutes),
  };
}

/**
 * Timesheet header totals — prefer API meta for the full filtered range (not just current page).
 */
export function resolveTimesheetSummaryFromMetaOrRows(meta, groups) {
  const loggedFromMeta = meta?.totalLoggedHours;
  const billableFromMeta = meta?.totalBillableHours;

  if (loggedFromMeta != null || billableFromMeta != null) {
    return {
      total: loggedFromMeta != null ? formatDurationWithUnit(loggedFromMeta) : "0m",
      billable: billableFromMeta != null ? formatDurationWithUnit(billableFromMeta) : "0m",
    };
  }

  return sumTimesheetGroupEntries(groups);
}

/**
 * Prefer API meta totals; otherwise sum table rows.
 * @param {object|null|undefined} meta
 * @param {unknown[]} rows
 * @param {(row: unknown) => number} getRowMinutes
 */
export function resolveGrandTotalFromMetaOrRows(meta, rows, getRowMinutes) {
  const totalHours = meta?.totalHours;
  if (totalHours != null && String(totalHours).trim() !== "") {
    return {
      totalLabel: formatDurationWithUnit(totalHours),
      totalMinutes: parseDurationToMinutes(totalHours),
    };
  }

  const grandTotalFormatted = meta?.grandTotalFormatted;
  if (grandTotalFormatted != null && String(grandTotalFormatted).trim() !== "") {
    const totalMinutes =
      meta?.grandTotalMinutes != null && Number.isFinite(Number(meta.grandTotalMinutes))
        ? Math.max(0, Math.round(Number(meta.grandTotalMinutes)))
        : parseDurationToMinutes(grandTotalFormatted);
    return {
      totalLabel: formatDurationWithUnit(grandTotalFormatted),
      totalMinutes,
    };
  }

  const grandTotalMinutes = meta?.grandTotalMinutes;
  if (grandTotalMinutes != null && Number.isFinite(Number(grandTotalMinutes))) {
    const totalMinutes = Math.max(0, Math.round(Number(grandTotalMinutes)));
    return {
      totalLabel: formatDurationWithUnit(totalMinutes),
      totalMinutes,
    };
  }

  const metaMinutes = meta?.totalMinutes ?? meta?.total_minutes;
  if (metaMinutes != null && Number.isFinite(Number(metaMinutes))) {
    const totalMinutes = Math.max(0, Math.round(Number(metaMinutes)));
    const totalFormatted = meta?.totalFormatted ?? meta?.totalLogged;
    return {
      totalLabel: totalFormatted
        ? formatDurationWithUnit(totalFormatted)
        : formatDurationWithUnit(totalMinutes),
      totalMinutes,
    };
  }

  let totalMinutes = 0;
  for (const row of Array.isArray(rows) ? rows : []) {
    totalMinutes += getRowMinutes(row);
  }

  return {
    totalLabel: formatDurationWithUnit(totalMinutes),
    totalMinutes,
  };
}
